/**
 * Kanban Component
 *
 * Modern Kanban board following 2025 design patterns:
 * - Clean card design with visual hierarchy
 * - Assignee avatars
 * - Priority indicators as colored badges
 * - Smart due date formatting with overdue indicators
 * - Subtle hover effects and transitions
 * - Virtualization for large datasets
 *
 * Design patterns applied:
 * - Card-based information architecture
 * - Color coding for priority/status
 * - Progressive disclosure for large columns
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Typography, Empty, Alert, Skeleton, Tooltip, Badge } from 'antd';
import {
  MessageOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useHistory } from 'react-router-dom';
import { urgencyTypes, urgencyColors, statusTypes } from '../../configs/task.config';
import {
  STATUS_CONFIG,
  PRIORITY_CSS_CLASS,
} from '../../utils/taskTableUtils';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { text } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Title } = Typography;

// Get all status columns from config, but prioritize main ones
const mainStatusOrder = [
  statusTypes['Not Started'],
  statusTypes['In Progress'],
  statusTypes['Completed'],
];
const allStatusKeys = Object.values(statusTypes);
const uniqueStatusKeys = [
  ...mainStatusOrder,
  ...allStatusKeys.filter((s) => !mainStatusOrder.includes(s)),
];
const statusColumns = uniqueStatusKeys.map((key) => {
  const label = Object.keys(statusTypes).find((k) => statusTypes[k] === key) || key;
  const config = STATUS_CONFIG[key] || { color: text.tertiary, label };
  return { key, label, color: config.color };
});

// Helper to get urgency color from config
const getUrgencyColor = (urgency) => {
  if (!urgency) return urgencyColors[urgencyTypes.Low];
  const match = Object.values(urgencyTypes).find((t) => t.toLowerCase() === urgency.toLowerCase());
  return urgencyColors[match] || urgencyColors[urgencyTypes.Low];
};

// Helper to get urgency label
const getUrgencyLabel = (urgency) => {
  if (!urgency) return 'Low';
  return urgency.charAt(0).toUpperCase() + urgency.slice(1).toLowerCase().replace(/_/g, ' ');
};

// Terminal statuses that should never show as overdue
const TERMINAL_STATUSES = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE', 'NO_LONGER_NEEDED', 'DUPLICATE'];

// Helper to format relative time for due dates
const formatDueDate = (dueDate, status) => {
  if (!dueDate) return { text: null, isOverdue: false };

  const date = new Date(dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dueDateNormalized = new Date(date);
  dueDateNormalized.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((dueDateNormalized - now) / 86400000);

  // Don't mark as overdue if task is completed/canceled
  const isTerminal = status && TERMINAL_STATUSES.includes(status.toUpperCase());
  const isOverdue = diffDays < 0 && !isTerminal;

  let text;
  if (diffDays === 0) {
    text = 'Today';
  } else if (diffDays === 1) {
    text = 'Tomorrow';
  } else if (diffDays === -1) {
    text = 'Yesterday';
  } else if (diffDays > 0 && diffDays < 7) {
    text = date.toLocaleDateString('en-US', { weekday: 'short' });
  } else if (diffDays < 0 && diffDays > -7) {
    text = isOverdue ? `${Math.abs(diffDays)}d overdue` : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    text = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return { text, isOverdue, diffDays };
};

// Modern Task Card Component
const TaskCard = ({ task, columnLabel, onTaskClick, setError }) => {
  // Check if task is completed/canceled (terminal status)
  const isCompleted = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE'].includes(task.status?.toUpperCase());

  // Pass status to formatDueDate so it excludes completed from overdue
  const dueInfo = formatDueDate(task.dueDate, task.status);
  const urgencyColor = getUrgencyColor(task.urgency);
  const urgencyLabel = getUrgencyLabel(task.urgency);
  // Support both assigneeUsers (array) and assigneeUser (single)
  const assignees = task.assigneeUsers || (task.assigneeUser ? [task.assigneeUser] : []);
  const commentCount = task.comments?.length || 0;

  // Build dynamic card classes
  const cardClasses = ['kanban-card-modern'];

  // Priority class
  const urgency = task.urgency?.toUpperCase() || 'LOW';
  const priorityClass = PRIORITY_CSS_CLASS[urgency];
  if (priorityClass) {
    cardClasses.push(`kanban-card-modern--priority-${priorityClass}`);
  }

  // Overdue class (formatDueDate now handles completed check, but double-check here)
  if (dueInfo.isOverdue && !isCompleted) {
    cardClasses.push('kanban-card-modern--overdue');
  }

  // Completed class
  if (isCompleted) {
    cardClasses.push('kanban-card-modern--completed');
  }

  return (
    <div
      role="listitem"
      className={cardClasses.join(' ')}
      onClick={() => {
        try {
          if (!task.id) {
            setError('Invalid task ID. Cannot navigate to details.');
            return;
          }
          onTaskClick(task.id);
        } catch (err) {
          console.error('Navigation error:', err);
          setError('Failed to navigate to task details.');
        }
      }}
      aria-label={`Task: ${task.name || 'Untitled Task'}. ${
        dueInfo.text ? `Due: ${dueInfo.text}.` : ''
      } Priority: ${urgencyLabel}. Click to view details.`}
    >
      {/* Urgency indicator stripe */}
      <div
        className="kanban-card__urgency-stripe"
        style={{ backgroundColor: urgencyColor }}
      />

      <div className="kanban-card__content">
        {/* Task Name */}
        <h4 className="kanban-card__title" title={task.name || 'Untitled Task'}>
          {task.name || 'Untitled Task'}
        </h4>

        {/* Task metadata row */}
        <div className="kanban-card__meta">
          {/* Due Date */}
          {dueInfo.text && (
            <Tooltip title={task.dueDate ? new Date(task.dueDate).toLocaleDateString() : ''}>
              <span
                className={`kanban-card__due ${dueInfo.isOverdue ? 'kanban-card__due--overdue' : ''}`}
              >
                {dueInfo.isOverdue ? (
                  <ExclamationCircleOutlined className="kanban-card__due-icon" />
                ) : (
                  <CalendarOutlined className="kanban-card__due-icon" />
                )}
                {dueInfo.text}
              </span>
            </Tooltip>
          )}

          {/* Comment count */}
          {commentCount > 0 && (
            <span className="kanban-card__comments">
              <MessageOutlined />
              {commentCount}
            </span>
          )}
        </div>

        {/* Bottom row: Assignees + Priority badge */}
        <div className="kanban-card__footer">
          {/* Assignees */}
          <div className="kanban-card__assignees">
            {assignees.length > 0 ? (
              <>
                {assignees.slice(0, 2).map((assignee) => {
                  const name = assignee.name || assignee.username || '';
                  return (
                    <Tooltip key={assignee.id} title={name}>
                      <div className="kanban-card__assignee-avatar">
                        <AvatarS3
                          imgKey={assignee.avatar?.key}
                          username={name}
                          size={24}
                        />
                      </div>
                    </Tooltip>
                  );
                })}
                {assignees.length > 2 && (
                  <Tooltip title={assignees.slice(2).map(a => a.name || a.username).join(', ')}>
                    <div className="kanban-card__assignee-overflow">
                      +{assignees.length - 2}
                    </div>
                  </Tooltip>
                )}
              </>
            ) : (
              <span className="kanban-card__unassigned">Unassigned</span>
            )}
          </div>

          {/* Priority badge */}
          <span
            className="kanban-card__priority"
            style={{
              backgroundColor: `${urgencyColor}15`,
              color: urgencyColor,
              borderColor: urgencyColor,
            }}
          >
            {urgencyLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

// Virtualized column component with expand/collapse functionality
const VirtualizedColumnWithExpansion = ({
  col,
  tasks,
  expandedColumns,
  setExpandedColumns,
  setError,
  history,
}) => {
  const maxInitialTasks = 30;
  const isExpanded = expandedColumns[col.key];
  const visibleTasks = isExpanded ? tasks : tasks.slice(0, maxInitialTasks);
  const remainingTasks = tasks.length - visibleTasks.length;

  const handleTaskClick = (taskId) => {
    history.push(`/tasks/details/${taskId}`);
  };

  return (
    <div
      role="list"
      aria-label={`Tasks in ${col.label} column (showing ${visibleTasks.length} of ${tasks.length})`}
    >
      {visibleTasks.map((task) => {
        if (!task || !task.id) {
          console.warn('Invalid task data:', task);
          return null;
        }

        return (
          <TaskCard
            key={task.id}
            task={task}
            columnLabel={col.label}
            onTaskClick={handleTaskClick}
            setError={setError}
          />
        );
      }).filter(Boolean)}

      {/* Show More / Show Less buttons */}
      {remainingTasks > 0 && (
        <div className="kanban-column__expand">
          <button
            className="kanban-column__expand-btn"
            onClick={() => {
              const newExpandedColumns = {
                ...expandedColumns,
                [col.key]: !expandedColumns[col.key],
              };
              setExpandedColumns(newExpandedColumns);
              try {
                localStorage.setItem('kanban-expanded-columns', JSON.stringify(newExpandedColumns));
              } catch (err) {
                console.warn('Failed to save expanded columns state:', err);
              }
            }}
          >
            <ExpandOutlined />
            Show {remainingTasks} more
          </button>
          <span className="kanban-column__expand-info">
            Showing {visibleTasks.length} of {tasks.length}
          </span>
        </div>
      )}

      {/* Show "All tasks visible" when fully expanded */}
      {remainingTasks === 0 && tasks.length > maxInitialTasks && (
        <div className="kanban-column__expanded">
          <span>All {tasks.length} tasks visible</span>
          <button
            className="kanban-column__collapse-btn"
            onClick={() => {
              const newExpandedColumns = {
                ...expandedColumns,
                [col.key]: false,
              };
              setExpandedColumns(newExpandedColumns);
              try {
                localStorage.setItem('kanban-expanded-columns', JSON.stringify(newExpandedColumns));
              } catch (err) {
                console.warn('Failed to save expanded columns state:', err);
              }
            }}
          >
            <CompressOutlined />
            Collapse
          </button>
        </div>
      )}
    </div>
  );
};

const Kanban = ({ tasks }) => {
  const { task: contextTask, tasksLoading } = useTaskContext();
  const history = useHistory();
  const task = Array.isArray(tasks) ? tasks : contextTask;

  // Local error state
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const kanbanRef = useRef(null);

  // Virtualization state
  const [enableVirtualization, setEnableVirtualization] = useState(false);
  const [expandedColumns, setExpandedColumns] = useState(() => {
    try {
      const stored = localStorage.getItem('kanban-expanded-columns');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Error boundary effect
  useEffect(() => {
    if (task && Array.isArray(task) && task.length === 0 && !tasksLoading) {
      setError(null);
    }
  }, [task, tasksLoading]);

  // Virtualization detection - enable for large datasets
  useEffect(() => {
    const totalTasks = task ? task.length : 0;
    const shouldVirtualize = totalTasks > 50;
    setEnableVirtualization(shouldVirtualize);
  }, [task]);

  // Group tasks by status (case-insensitive match) and sort by due date
  const grouped = useMemo(() => {
    if (!Array.isArray(task)) {
      return {};
    }

    try {
      const filteredTasks = (colKey) => {
        return task.filter((t) => {
          if (!t || typeof t !== 'object') return false;
          return (t.status || '').toUpperCase() === colKey.toUpperCase();
        });
      };

      return statusColumns.reduce((acc, col) => {
        try {
          const sortedTasks = filteredTasks(col.key)
            .slice()
            .sort((a, b) => {
              try {
                const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
                const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;

                if (isNaN(aDate) && isNaN(bDate)) return 0;
                if (isNaN(aDate)) return 1;
                if (isNaN(bDate)) return -1;

                return aDate - bDate;
              } catch (sortErr) {
                console.warn('Error sorting tasks:', sortErr);
                return 0;
              }
            });
          acc[col.key] = sortedTasks;
        } catch (columnErr) {
          console.warn(`Error processing column ${col.key}:`, columnErr);
          acc[col.key] = [];
        }
        return acc;
      }, {});
    } catch (err) {
      console.error('Error grouping tasks:', err);
      setError('Failed to process task data. Please refresh the page.');
      return {};
    }
  }, [task]);

  // Handle retry functionality
  const handleRetry = async () => {
    setIsRetrying(true);
    setError(null);
    try {
      setTimeout(() => {
        setIsRetrying(false);
      }, 1000);
    } catch (err) {
      setError('Failed to retry loading tasks. Please try again.');
      setIsRetrying(false);
    }
  };

  const handleTaskClick = (taskId) => {
    history.push(`/tasks/details/${taskId}`);
  };

  // Show error state
  if (error) {
    return (
      <div className="kanban-error-container">
        <Alert
          message="Error Loading Tasks"
          description={error}
          type="error"
          showIcon
          action={
            <button onClick={handleRetry} disabled={isRetrying} className="kanban-retry-button">
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          }
        />
      </div>
    );
  }

  // Show loading spinner if tasks are being fetched
  if (tasksLoading || isRetrying) {
    return (
      <div className="kanban-board-modern">
        <div className="kanban-board__header">
          <Skeleton.Input style={{ width: 150 }} active />
        </div>
        <div className="kanban-board__columns">
          {statusColumns.slice(0, 4).map((_, index) => (
            <div key={index} className="kanban-column-modern">
              <div className="kanban-column__header">
                <Skeleton.Input style={{ width: 100 }} active />
              </div>
              <div className="kanban-column__body">
                {[...Array(3)].map((_, cardIndex) => (
                  <div key={cardIndex} className="kanban-card-skeleton">
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Defensive: if no tasks, show empty
  if (!Array.isArray(task)) {
    const errorMessage = 'Unable to load tasks. Please refresh the page or contact support.';
    setError(errorMessage);
    return <Empty description="No tasks" />;
  }

  // Calculate total tasks
  const kanbanTotal = task.length;

  return (
    <div
      ref={kanbanRef}
      role="application"
      aria-label="Task management kanban board"
      className="kanban-board-modern"
    >
      {/* Accessibility instructions - hidden but readable by screen readers */}
      <div className="sr-only" aria-live="polite">
        Kanban board with {kanbanTotal} tasks organized in {statusColumns.length} columns.
        {enableVirtualization && 'Performance mode enabled for large task list. '}
        Click on any task to view details.
      </div>

      {/* Board Header */}
      <div className="kanban-board__header">
        <span className="kanban-board__total">
          {kanbanTotal} {kanbanTotal === 1 ? 'task' : 'tasks'}
        </span>
        {enableVirtualization && (
          <Badge
            status="success"
            text="Optimized"
            className="kanban-board__optimized"
          />
        )}
      </div>

      {/* Kanban Columns */}
      <div role="grid" aria-label="Task columns" className="kanban-board__columns">
        {statusColumns.map((col, columnIndex) => (
          <div
            key={col.key}
            role="gridcell"
            aria-label={`${col.label} column with ${
              grouped[col.key] ? grouped[col.key].length : 0
            } tasks`}
            className="kanban-column-modern"
          >
            {/* Column Header */}
            <div className="kanban-column__header">
              <div className="kanban-column__header-left">
                <span
                  className="kanban-column__status-dot"
                  style={{ backgroundColor: col.color }}
                />
                <h3 className="kanban-column__title" id={`column-${columnIndex}-title`}>
                  {col.label}
                </h3>
              </div>
              <span className="kanban-column__count">
                {grouped[col.key]?.length || 0}
              </span>
            </div>

            {/* Column Body */}
            <div
              className="kanban-column__body"
              role="region"
              aria-labelledby={`column-${columnIndex}-title`}
            >
              {enableVirtualization && grouped[col.key] && grouped[col.key].length > 50 ? (
                <VirtualizedColumnWithExpansion
                  col={col}
                  tasks={grouped[col.key]}
                  expandedColumns={expandedColumns}
                  setExpandedColumns={setExpandedColumns}
                  setError={setError}
                  history={history}
                />
              ) : (
                <div role="list" aria-label={`Tasks in ${col.label} column`}>
                  {grouped[col.key] && grouped[col.key].length > 0 ? (
                    grouped[col.key].map((taskItem) => {
                      if (!taskItem || !taskItem.id) {
                        console.warn('Invalid task data:', taskItem);
                        return null;
                      }

                      return (
                        <TaskCard
                          key={taskItem.id}
                          task={taskItem}
                          columnLabel={col.label}
                          onTaskClick={handleTaskClick}
                          setError={setError}
                        />
                      );
                    }).filter(Boolean)
                  ) : (
                    <Empty
                      description="No tasks"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      className="kanban-column__empty"
                      role="status"
                      aria-label={`No tasks in ${col.label} column`}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

Kanban.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      status: PropTypes.string,
      urgency: PropTypes.string,
      dueDate: PropTypes.string,
      assigneeUsers: PropTypes.array,
      comments: PropTypes.array,
    })
  ),
};

Kanban.defaultProps = {
  tasks: [],
};

export default Kanban;
